import React, { useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import { formatDay, formatMoney } from '../features/featureUi';

interface Line { code: string; name: string; perUser: boolean; unitPrice: number; quantity: number; amount: number; }

export interface SubscriptionSummary {
    status: 'PRUEBA' | 'ACTIVA' | 'MOROSA' | 'SUSPENDIDA';
    users: number;              // usuarios que se cobran (contratados o, si hay más, los activos)
    contractedUsers: number;
    usersInUse: number;
    trialEndsOn: string | null;
    nextChargeOn: string;
    amount: number;
    currency: string;
    lines: Line[];
}

const STATUS: Record<SubscriptionSummary['status'], { label: string; css: string }> = {
    PRUEBA: { label: 'Periodo de prueba', css: 'bg-info-subtle text-info-emphasis' },
    ACTIVA: { label: 'Al día', css: 'bg-success-subtle text-success-emphasis' },
    MOROSA: { label: 'Cobro pendiente', css: 'bg-warning-subtle text-warning-emphasis' },
    SUSPENDIDA: { label: 'Suspendida', css: 'bg-danger-subtle text-danger-emphasis' },
};

const sum = (lines: Line[]) => Math.round(lines.reduce((total, l) => total + l.amount, 0) * 100) / 100;

interface Props {
    merchantId: number | null;  // comercio de la lista (el de la sesión o el filtrado por banco/plataforma)
    refreshKey?: unknown;       // al cambiar (usuarios creados o eliminados) se vuelve a consultar
}

/**
 * Encabezado de la página de usuarios para comercios con suscripción: cantidad de usuarios, costo de las
 * funcionalidades por usuario (retail, inventario), funcionalidades adicionales y fecha de renovación.
 * Sin suscripción no muestra nada.
 */
export const SubscriptionHeader = ({ merchantId, refreshKey }: Props) => {
    const [data, setData] = useState<SubscriptionSummary | null>(null);

    useEffect(() => {
        setData(null);
        if (!merchantId) return;
        let active = true;
        topApi.get<{ subscription: SubscriptionSummary | null }>('/merchant-subscription/summary', { params: { merchantId } })
            .then(resp => { if (active) setData(resp.data?.subscription ?? null); })
            .catch(() => undefined);
        return () => { active = false; };
    }, [merchantId, refreshKey]);

    if (!data) return null;

    const perUser = data.lines.filter(l => l.perUser);
    const extras = data.lines.filter(l => !l.perUser);
    const unitPerUser = Math.round(perUser.reduce((total, l) => total + l.unitPrice, 0) * 100) / 100;
    const additional = data.usersInUse - data.contractedUsers;
    const status = STATUS[data.status] ?? STATUS.ACTIVA;
    const inTrial = data.status === 'PRUEBA' && !!data.trialEndsOn;

    return (
        <section className="card border-0 shadow-sm mb-4" aria-label="Suscripción del comercio">
            <div className="card-body">
                <div className="row g-3 align-items-stretch">
                    <div className="col-sm-6 col-xl-3">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-people me-1" />Usuarios</div>
                        <div className="fs-3 fw-semibold lh-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{data.usersInUse}</div>
                        <div className="small text-muted mt-1">
                            {data.contractedUsers} contratados
                            {additional > 0 && <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis ms-2">+{additional} adicionales</span>}
                        </div>
                    </div>

                    <div className="col-sm-6 col-xl-3">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-shop me-1" />{perUser.map(l => l.name).join(' + ') || 'Por usuario'}</div>
                        <div className="fs-3 fw-semibold lh-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(sum(perUser), data.currency)}</div>
                        <div className="small text-muted mt-1">
                            {perUser.length ? `${data.users} ${data.users === 1 ? 'usuario' : 'usuarios'} × ${formatMoney(unitPerUser, data.currency)}` : 'Sin cobro por usuario'}
                        </div>
                    </div>

                    <div className="col-sm-6 col-xl-3">
                        <div className="small text-muted text-uppercase mb-1"><i className="bi bi-puzzle me-1" />Funcionalidades adicionales</div>
                        <div className="fs-3 fw-semibold lh-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(sum(extras), data.currency)}</div>
                        <div className="small text-muted mt-1 text-truncate" title={extras.map(l => l.name).join(', ')}>
                            {extras.length ? extras.map(l => l.name).join(', ') : 'Ninguna'}
                        </div>
                    </div>

                    <div className="col-sm-6 col-xl-3">
                        <div className="small text-muted text-uppercase mb-1">
                            <i className="bi bi-arrow-repeat me-1" />{inTrial ? 'Fin de la prueba' : 'Se renueva'}
                        </div>
                        <div className="fs-5 fw-semibold lh-sm">{formatDay(inTrial ? data.trialEndsOn : data.nextChargeOn)}</div>
                        <div className="small mt-1 d-flex flex-wrap align-items-center gap-2">
                            <span className="text-muted">Total <strong className="text-body">{formatMoney(data.amount, data.currency)}</strong>/mes</span>
                            <span className={`badge rounded-pill ${status.css}`}>{status.label}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SubscriptionHeader;
